import * as React from 'react';
import { CircularProgress, Typography } from '@material-ui/core';
import Button from '@components/common/button/Button';
import Dialog from '@components/common/dialog/dialog.component';
import TranslateNew from '@translate/TranslateNew';
import { buttonContained, buttonOutlined } from 'src/main.style';
import { getCurrentLocation, IDeviceLocation, LocationFailure, LocationResult } from '@helpers/locationHelper';
import { canOpenNativeSettings, openNativeSettings } from '@helpers/nativeSettings';

const tn: TranslateNew = TranslateNew.getInstance();

type Step = 'CONFIRM' | 'LOCATING' | LocationFailure;

interface IProps {
  open: boolean;
  onClose: () => void;
  onShare: (location: IDeviceLocation) => void;
}

interface IState {
  step: Step;
}

/**
 * Asks before the user's location is shared in a chat, then looks it up. Nothing asks for location
 * access until the user has said yes here.
 */
export default class ShareLocationDialog extends React.Component<IProps, IState> {
  // Bumped whenever a running lookup stops mattering, so a position that arrives after a cancel is dropped.
  private lookup: number = 0;

  constructor(props: IProps) {
    super(props);
    this.state = {step: 'CONFIRM'};
  }

  public componentDidUpdate(oldProps: IProps) {
    if (this.props.open && !oldProps.open) {
      this.setState({step: 'CONFIRM'});
    }
  }

  public componentWillUnmount() {
    this.lookup += 1;
  }

  public render() {
    const {step} = this.state;

    return (
      <Dialog
        open={this.props.open}
        onClose={this.close}
        title={step === 'DENIED' ? tn.lang.chatCheckInDeniedTitle : tn.lang.chatCheckInTitle}
        content={this.renderContent()}
        actions={this.renderActions()}
      />
    );
  }

  private renderContent(): JSX.Element {
    switch (this.state.step) {
      case 'LOCATING':
        // The question stays, so the dialog keeps its size. The row clips the spinning box of the
        // progress circle, which would otherwise flash a scrollbar in the dialog.
        return (
          <>
            <Typography>{tn.lang.chatCheckInConfirm}</Typography>
            <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px', overflow: 'hidden'}}>
              <CircularProgress size={20} />
              <Typography>{tn.lang.chatCheckInLocating}</Typography>
            </div>
          </>
        );
      case 'DENIED':
        return <Typography>{canOpenNativeSettings() ? tn.lang.chatCheckInDenied : tn.lang.chatCheckInDeniedBrowser}</Typography>;
      case 'UNAVAILABLE':
        return <Typography>{tn.lang.chatCheckInUnavailable}</Typography>;
      default:
        return <Typography>{tn.lang.chatCheckInConfirm}</Typography>;
    }
  }

  private renderActions(): JSX.Element {
    switch (this.state.step) {
      case 'LOCATING':
        return <Button variant='outlined' style={buttonOutlined} onClick={this.close}>{tn.lang.cancel}</Button>;
      case 'DENIED':
        return (
          <>
            <Button variant='outlined' style={buttonOutlined} onClick={this.close}>{tn.lang.close}</Button>
            {canOpenNativeSettings() && (
              <Button variant='contained' color='primary' style={buttonContained}
                onClick={() => {this.close(); openNativeSettings();}}>
                {tn.lang.settings}
              </Button>
            )}
          </>
        );
      case 'UNAVAILABLE':
        return (
          <>
            <Button variant='outlined' style={buttonOutlined} onClick={this.close}>{tn.lang.close}</Button>
            <Button variant='contained' color='primary' style={buttonContained} onClick={this.locate}>{tn.lang.tryAgain}</Button>
          </>
        );
      default:
        return (
          <>
            <Button variant='outlined' style={buttonOutlined} onClick={this.close}>{tn.lang.no}</Button>
            <Button variant='contained' color='primary' style={buttonContained} onClick={this.locate}>{tn.lang.yes}</Button>
          </>
        );
    }
  }

  private locate = () => {
    this.lookup += 1;
    const lookup = this.lookup;
    this.setState({step: 'LOCATING'});

    getCurrentLocation()
      .catch((): LocationResult => ({ok: false, failure: 'UNAVAILABLE'}))
      .then((result) => {
        if (lookup !== this.lookup) {
          return;
        }
        if (result.ok) {
          this.props.onShare(result.location);
        } else {
          this.setState({step: result.failure});
        }
      });
  }

  private close = () => {
    this.lookup += 1;
    this.props.onClose();
  }
}



// WEBPACK FOOTER //
// ./src/components/chat/ShareLocationDialog.tsx